import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Switch } from '@cotera/client/v0/components/ui/switch';
import { cn } from '@cotera/client/v0/lib/utils';
// Imported from the module rather than the `components/app` barrel: that barrel
// pulls in the whole ui barrel, which re-exports this grid.
import {
  ModalPageView,
  useOptionalModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { ALIGNMENT_CLASS, formatDataGridNumber } from '../format';
import type {
  DataGridColumnAlignment,
  DataGridColumnDataType,
  DataGridColumnDisplayOptions,
  DataGridNumberFormat,
  DataGridRowHeight,
} from '../types';
import { DATA_GRID_ROW_HEIGHTS } from '../types';
import { Segmented } from './segmented';

const NUMBER_FORMATS: { value: DataGridNumberFormat; label: string }[] = [
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'compact', label: 'Compact' },
];

const ALIGNMENTS: { value: DataGridColumnAlignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const DECIMALS: { value: number | null; label: string }[] = [
  { value: null, label: 'Auto' },
  { value: 0, label: '0' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
];

const PREVIEW_VALUE = 0.4213;

/**
 * What the preview shows for a column the number controls do not apply to:
 * long enough that the tall preset visibly wraps it and the shorter ones
 * visibly cut it off, which is the whole difference between them.
 */
const PREVIEW_TEXT =
  'A longer cell value, the kind a compact row cuts off and a tall row shows in full.';

/** The label every control in these header dialogs is titled with. */
const FIELD_LABEL_CLASS =
  'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

/**
 * Number formatting is spent on numbers. Offering "percent" and a decimal
 * count on a text column made the dialog look like it did nothing.
 */
const isNumericColumn = (type: DataGridColumnDataType | undefined): boolean =>
  type === 'number' || type === undefined || type === 'unknown';

const DataGridDisplayOptionsForm: React.FC<{
  columnLabel: string;
  columnType: DataGridColumnDataType | undefined;
  /** Where the controls start; the form owns their state from then on. */
  initialOptions: DataGridColumnDisplayOptions;
  onChange: (options: Partial<DataGridColumnDisplayOptions>) => void;
  /**
   * The grid's row height, to size the preview by. Read-only: row height is
   * grid-wide and lives in the dataset menu, so this dialog shows what a row
   * currently looks like without offering to change it.
   */
  rowHeight: DataGridRowHeight;
  onDone: () => void;
}> = ({
  columnLabel,
  columnType,
  initialOptions,
  onChange,
  rowHeight,
  onDone,
}) => {
  // The form drives its own display and writes each change through to the grid.
  // It cannot read the answer back out of props: hosted in the app's modal
  // stack, this view is rendered by the stack rather than by the header, so a
  // prop that changed after the push never reaches it and every control would
  // sit on the value it opened with while the grid moved underneath.
  const [options, setOptions] =
    React.useState<DataGridColumnDisplayOptions>(initialOptions);

  const changeOptions = (next: Partial<DataGridColumnDisplayOptions>) => {
    setOptions((current) => ({ ...current, ...next }));
    onChange(next);
  };

  const numeric = isNumericColumn(columnType);
  const previewShare =
    numeric && options.inCellBar === true
      ? Math.min(1, Math.max(0, PREVIEW_VALUE / (options.barMax ?? 1)))
      : null;

  return (
    <div className="flex flex-col gap-4">
      {numeric ? (
        <>
          <Segmented
            label="Number format"
            value={options.numberFormat}
            options={NUMBER_FORMATS}
            onChange={(numberFormat) => changeOptions({ numberFormat })}
          />
          <Segmented
            label="Decimals"
            value={options.decimals}
            options={DECIMALS}
            onChange={(decimals) => changeOptions({ decimals })}
          />
        </>
      ) : null}
      <Segmented
        label="Alignment"
        value={options.alignment}
        options={ALIGNMENTS}
        onChange={(alignment) => changeOptions({ alignment })}
      />

      {numeric ? (
        <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
          <div>
            <div className="text-xs font-medium text-foreground">
              In-cell bar
            </div>
            <div className="text-[11px] text-muted-foreground">
              Draw a proportional bar behind {columnLabel}
            </div>
          </div>
          <Switch
            checked={options.inCellBar}
            aria-label="In-cell bar"
            onCheckedChange={(inCellBar) => changeOptions({ inCellBar })}
          />
        </div>
      ) : null}

      <div className="space-y-1.5">
        <div className={FIELD_LABEL_CLASS}>Preview</div>
        {/* Sized to the grid's current row height, so what is shown here is
            what a row actually looks like rather than a generic swatch. */}
        <div
          className={cn(
            'relative flex overflow-hidden rounded-lg border border-border bg-muted/40 px-3',
            rowHeight === 'tall' ? 'items-start py-2' : 'items-center'
          )}
          style={{ height: DATA_GRID_ROW_HEIGHTS[rowHeight] }}
        >
          {previewShare !== null ? (
            <div
              className="absolute inset-y-1 left-1 rounded bg-primary/15"
              style={{ width: `${previewShare * 100}%` }}
            />
          ) : null}
          <span
            className={cn(
              'relative flex w-full text-sm',
              numeric ? 'tabular-nums' : '',
              rowHeight === 'tall'
                ? 'line-clamp-3 whitespace-pre-wrap break-words'
                : 'truncate',
              ALIGNMENT_CLASS[options.alignment]
            )}
          >
            {numeric
              ? formatDataGridNumber(PREVIEW_VALUE, options)
              : PREVIEW_TEXT}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
};

export type DataGridDisplayOptionsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnLabel: string;
  /** Decides which of the column controls are worth showing. */
  columnType?: DataGridColumnDataType;
  options: DataGridColumnDisplayOptions;
  onChange: (options: Partial<DataGridColumnDisplayOptions>) => void;
  /** Grid-wide; shown here only to size the preview. */
  rowHeight: DataGridRowHeight;
};

/**
 * Column presentation editor. Every change writes straight through to the
 * grid's watchables so cells reformat without a remount.
 *
 * Only this column's settings. Row height used to sit here too, which put a
 * grid-wide control inside a per-column dialog and needed a caption
 * apologising for it — and left the same setting reachable from two places. It
 * lives in the dataset menu now, and all this keeps of it is the preview's
 * height.
 *
 * Like the filter editor beside it, it prefers the app's modal stack so it
 * layers and is surfaced like every other app modal; when the grid renders
 * without one (tests, isolated embeds) it falls back to its own dialog rather
 * than losing the affordance.
 */
export const DataGridDisplayOptionsDialog: React.FC<
  DataGridDisplayOptionsDialogProps
> = (props) => {
  const {
    open,
    onOpenChange,
    columnLabel,
    columnType,
    options,
    onChange,
    rowHeight,
  } = props;
  const modal = useOptionalModalManager();
  // The pushed view outlives this render, so it reads the current props through
  // a ref instead of capturing them — otherwise every parent render would have
  // to pop and re-push to stay current.
  const latest = React.useRef(props);
  React.useEffect(() => {
    latest.current = props;
  });

  React.useEffect(() => {
    if (!open || modal === null) {
      return;
    }
    modal.push({
      view: () => (
        <ModalPageView
          title="Display options"
          subtitle={latest.current.columnLabel}
          className="px-4 py-4"
        >
          <DataGridDisplayOptionsForm
            columnLabel={latest.current.columnLabel}
            columnType={latest.current.columnType}
            initialOptions={latest.current.options}
            onChange={(next) => latest.current.onChange(next)}
            rowHeight={latest.current.rowHeight}
            onDone={() => latest.current.onOpenChange(false)}
          />
        </ModalPageView>
      ),
      onClose: () => latest.current.onOpenChange(false),
    });
    return () => modal.pop();
  }, [open, modal]);

  if (modal !== null) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className={FIELD_LABEL_CLASS}>Display options</div>
          <DialogTitle className="truncate">{columnLabel}</DialogTitle>
        </DialogHeader>
        <DataGridDisplayOptionsForm
          columnLabel={columnLabel}
          columnType={columnType}
          initialOptions={options}
          onChange={onChange}
          rowHeight={rowHeight}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
